/**
 * Capacitor hardware-back-button bridge.
 *
 * Smart TV remotes and Android phones both fire the "back" event
 * through the Capacitor App plugin. Without an explicit handler the
 * plugin's default is to exit the activity — which on a TV means
 * tapping back ANYWHERE drops the user out to the launcher. This hook
 * routes back-presses through React Router's navigate(-1) instead, so
 * the back button feels native: it pops one screen at a time and only
 * exits the app once we're already on the main menu.
 */
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export default function useHardwareBackButton() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const App = window.Capacitor?.Plugins?.App;
    if (!App?.addListener) return;
    let handle;
    (async () => {
      handle = await App.addListener("backButton", () => {
        // If we're already on a top-level screen, fall through to the
        // default behaviour (which on TV closes the app) — otherwise
        // pop one screen at a time.
        const topLevel = ["/player", "/player/menu", "/player/activate"];
        if (topLevel.includes(location.pathname)) {
          App.exitApp?.();
          return;
        }
        navigate(-1);
      });
    })();
    return () => {
      try {
        handle?.remove?.();
      } catch {
        /* no-op */
      }
    };
  }, [location.pathname, navigate]);
}
